import { Cell } from 'utils/data'
import { IDetailedCartonCount, ILaborType } from './ITrailer'
import { Constants } from './messages'

export const extractLaborHours = (
    laborTypes: ILaborType[] | undefined | null,
    type: string,
): string => {
    if (!laborTypes) return ''
    return (
        laborTypes.find((labor: ILaborType) => labor.type === type)
            ?.formatted_hours ?? ''
    )
}

export const createCustomBlockRows = (
    detailCounts: IDetailedCartonCount[],
): Cell[][] => {
    return detailCounts.map((block: IDetailedCartonCount): Cell[] => {
        const { id, name, case_pack_counts, repack_counts, labor_types } = block

        const secondarySortingHours = extractLaborHours(
            labor_types,
            Constants.SECONDARY_SORTING,
        )
        const stockingHours = extractLaborHours(labor_types, Constants.STOCKING)

        return [
            { value: `${id}-${name}` },
            { value: `${case_pack_counts ?? 0}` },
            { value: `${repack_counts ?? 0}` },
            { value: stockingHours },
            { value: secondarySortingHours },
        ]
    })
}

export const createLoadZoneRows = (
    detailCounts: IDetailedCartonCount[],
): Cell[][] => {
    return detailCounts.map((block: any): any => {
        const { name, case_pack_counts, repack_counts, labor_types } = block
        const secondarySortingHours = extractLaborHours(
            labor_types,
            Constants.SECONDARY_SORTING,
        )
        const stockingHours = extractLaborHours(labor_types, Constants.STOCKING)

        return [
            { value: String(name) },
            { value: String(case_pack_counts ?? 0) },
            { value: String(repack_counts ?? 0) },
            { value: stockingHours },
            { value: secondarySortingHours },
        ]
    })
}

export const sortTableRows = (
    rows: Cell[][],
    fieldIndex: number,
    sortDirection: 'asc' | 'desc',
): Cell[][] => {
    return rows.sort((a, b) => {
        const isNumericField = fieldIndex === 1 || fieldIndex === 2
        const isCustomBlockField = fieldIndex === 0

        let aValue: number | string
        let bValue: number | string

        if (isCustomBlockField) {
            const aMatch = a[fieldIndex].value.match(/^(\d+)/)
            const bMatch = b[fieldIndex].value.match(/^(\d+)/)
            aValue = aMatch ? Number(aMatch[1]) : 0
            bValue = bMatch ? Number(bMatch[1]) : 0
        } else if (isNumericField) {
            aValue = Number(a[fieldIndex].value)
            bValue = Number(b[fieldIndex].value)
        } else {
            aValue = a[fieldIndex].value
            bValue = b[fieldIndex].value
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
        } else if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortDirection === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue)
        }
        return 0
    })
}

export const getFieldIndex = (field: string): number => {
    const { TABLE_KEYS } = Constants
    const fieldMapping: { [key: string]: number } = {
        [TABLE_KEYS.CUSTOM_BLOCK]: 0,
        [TABLE_KEYS.CASE_CARTONS]: 1,
        [TABLE_KEYS.REPACK_CARTONS]: 2,
        [TABLE_KEYS.STOCKING_TIME]: 3,
        [TABLE_KEYS.SORTING_TIME]: 4,
        [TABLE_KEYS.LOAD_ZONE]: 0,
    }
    return fieldMapping[field] ?? 0
}

export const getDataRows = (customBlockRows: Cell[][]) => {
    const customBlockRowsWithZero = customBlockRows.filter((block) =>
        block[0].value.startsWith('00'),
    )
    const customBlockRowsWithoutZero = customBlockRows.filter(
        (block) => !block[0].value.startsWith('00'),
    )

    return {
        customBlockRowsWithZero,
        customBlockRowsWithoutZero,
    }
}

export const createChildRowsForLoadZone = (
    item: any,
    expandedLoadZone: string,
): Cell[][] => {
    if (!Array.isArray(item.children) || typeof item.children[0] !== 'object') {
        return []
    }

    const sortedChildren = (item.children as IDetailedCartonCount[]).sort(
        (a, b) => {
            const aValue = a.case_pack_counts ?? 0
            const bValue = b.case_pack_counts ?? 0
            return bValue - aValue
        },
    )

    return createCustomBlockRows(sortedChildren).map((row: Cell[]) => {
        const [firstCell, ...rest] = row
        return [
            {
                ...firstCell,
                childOf: expandedLoadZone,
                link: undefined,
            },
            ...rest,
        ]
    })
}
